import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { fmtFecha } from '../lib/utils';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Search, Loader2, RefreshCw, DollarSign,
  CheckCircle, Package, Upload, Check, AlertTriangle, X, Plus, ShieldCheck,
  FileText, ClipboardList, ScanLine, Trash2, PackagePlus, Zap,
  AlertOctagon, Printer, FileSpreadsheet, Replace, MoreHorizontal,
} from 'lucide-react';
import { toast } from 'sonner';
import { FacturaViewer } from '../components/FacturaViewer';
import { OCViewer } from '../components/OCViewer';
import { useEmpresa } from '../hooks/useEmpresa';
import { OCExpressModal } from './contabilizar/OCExpressModal';
import { ContabResultModal } from './contabilizar/ContabResultModal';
import { PDFViewerModal } from './contabilizar/PDFViewerModal';
import { exportToCSV, printTable } from '../lib/printUtils';
import { AlegraStatusBadge } from '../components/dte/AlegraStatusBadge'; // Iter 235.f
import { StickyHScroll } from '../components/ui/sticky-hscroll'; // Iter 235.g
import { useRegisterCmdKCommands } from '../contexts/CmdKContext'; // Iter 235.h
import { useAuth } from '../lib/auth';

const fmt = (num) => Math.round(num || 0).toLocaleString('es-CL');

// ===== Authenticated PDF fetch (returns blob URL or null) =====
const fetchAuthPDF = async (url) => {
  try {
    // Strip /api prefix if present since api.get already has /api as baseURL
    const cleanUrl = url.startsWith('/api/') ? url.slice(4) : url;
    const res = await api.get(cleanUrl, { responseType: 'blob', validateStatus: (s) => s < 500 });
    if (res.status === 404 || res.status >= 400) {
      toast.error('Documento no encontrado');
      return null;
    }
    const contentType = res.headers?.['content-type'] || '';
    if (contentType.includes('json')) {
      toast.error('Documento no disponible');
      return null;
    }
    const blob = new Blob([res.data], { type: 'application/pdf' });
    return URL.createObjectURL(blob);
  } catch {
    toast.error('Error al abrir el documento');
    return null;
  }
};

// Iter 235.f (Carlos): badge unificado (reemplaza ContabBadge interno)
function ContabBadge({ contabilizada, eliminadaAlegra, alegra_id, estado_pago, en_nomina, liberada_para_pago }) {
  return (
    <AlegraStatusBadge
      contabilizada_en_alegra={Boolean(alegra_id) && contabilizada}
      contabilizada={contabilizada}
      eliminada_en_alegra={eliminadaAlegra}
      estado_pago={estado_pago || ''}
      alegra_bill_id={alegra_id || ''}
      en_nomina={en_nomina}
      liberada_para_pago={liberada_para_pago}
    />
  );
}

function MontoAlert({ validacion }) {
  if (!validacion) return null;
  if (validacion.proveedor_diferente) {
    return (
      <div className="mt-0.5 flex items-center gap-0.5 text-blue-600" data-testid="monto-alert-prov-diff">
        <AlertOctagon className="w-3 h-3 flex-shrink-0" />
        <span className="text-[8px] leading-tight">
          OC es de {validacion.oc_proveedor || 'otro proveedor'}
        </span>
      </div>
    );
  }
  if (!validacion.excede_monto) return null;
  return (
    <div className="mt-0.5 flex items-center gap-0.5 text-red-600" data-testid="monto-alert">
      <AlertOctagon className="w-3 h-3 flex-shrink-0" />
      <span className="text-[8px] leading-tight">
        Excede OC ${fmt(validacion.oc_neto)} en ${fmt(Math.abs(validacion.diferencia))}
      </span>
    </div>
  );
}

export default function FacturasContabilizar() {
  const empresa = useEmpresa();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState({});
  // Estado del filtro: 'pendientes' (default — solo VB para contabilizar) | 'contabilizadas' | 'todas'
  const [estadoFilter, setEstadoFilter] = useState('pendientes');
  const [contabilizando, setContabilizando] = useState(false);
  const [resultModal, setResultModal] = useState({ open: false, data: null });
  const [recepcionando, setRecepcionando] = useState(null);

  // DTE detail modal
  const [dteModal, setDteModal] = useState({ open: false, folio: null, data: null, loading: false });

  // OC detail modal (Issue #2 fix)
  const [ocModal, setOcModal] = useState({ open: false, data: null, loading: false, notFound: false, ocNumero: null, folio: null });

  // Add by folio
  const [addFolio, setAddFolio] = useState('');
  const [adding, setAdding] = useState(false);
  const [searchResult, setSearchResult] = useState(null);

  // OC Express modal - now with preview & account classification
  // Iter 235.i (Feb 2026): `reemplazar_oc_actual` se activa cuando el usuario declara
  // que la OC asociada NO corresponde y quiere crear una OC Express en su lugar.
  const [expressModal, setExpressModal] = useState({ open: false, folio: null, reemplazar_oc_actual: false, oc_anterior: null });
  const [expressOT, setExpressOT] = useState('');
  const [expressSolicitante, setExpressSolicitante] = useState(''); // Iter 389 (Renzo): obligatorio, pre-cargado con el usuario
  const { user: authUser } = useAuth();
  const [expressProcesando, setExpressProcesando] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [itemsCtas, setItemsCtas] = useState({}); // {linea: cta_code}
  const [itemsOts, setItemsOts] = useState({}); // {linea: ot_numero} per-item OT
  const [otList, setOtList] = useState([]);
  const [otFiltro, setOtFiltro] = useState('ejecucion');
  // Iter 365 — par canónico Condición + Medio de Pago (reemplaza forma_pago texto libre)
  const EXPRESS_CMP_VACIO = { condicion_codigo: '', medio_pago_codigo: '', requiere_medio_pago: false, condicion: null };
  const [expressCondMP, setExpressCondMP] = useState(EXPRESS_CMP_VACIO);

  // PDF viewer modal
  const [pdfModal, setPdfModal] = useState({ open: false, url: null, title: '', loading: false });

  // Descartadas
  const [descartadas, setDescartadas] = useState([]);
  const [showDescartadas, setShowDescartadas] = useState(false);

  // Lotes VB pendientes de contabilizar
  const [lotesVBPend, setLotesVBPend] = useState([]);
  const [contabLote, setContabLote] = useState(null); // lote being contabilized

  // Iter 235.j (Carlos): asociación 1-click a OC sugerida (best_match)
  const [asociandoOc, setAsociandoOc] = useState(false);
  const handleAsociarOC = async (folio, rutEmisor, numeroOc) => {
    if (asociandoOc) return;
    setAsociandoOc(true);
    try {
      await api.post('/contabilidad/acepta-rechazo-dte/asociar-oc', {
        folio,
        rut_emisor: rutEmisor,
        numero_oc: numeroOc,
      });
      toast.success(`✓ Factura ${folio} asociada a OC ${numeroOc}`);
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error asociando OC');
    } finally {
      setAsociandoOc(false);
    }
  };

  // Iter 235.l (Carlos): Capa 3 — asociación MASIVA por best_match ≥ score_min
  const [asociandoMasivo, setAsociandoMasivo] = useState(false);
  const handleAsociarMasivo = async (scoreMin = 90) => {
    if (asociandoMasivo) return;
    // Preview con dry_run para confirmar
    setAsociandoMasivo(true);
    try {
      const preview = await api.post('/compras/facturas-listas-contabilizar/asociar-masivo-best-match', {
        score_min: scoreMin, dry_run: true,
      });
      const candidatos = preview.data.candidatos || [];
      if (candidatos.length === 0) {
        toast.info(`No hay facturas con match ≥ ${scoreMin}%`);
        return;
      }
      const ok = window.confirm(
        `Se asociarán automáticamente ${candidatos.length} factura(s) con match ≥ ${scoreMin}%:\n\n` +
        candidatos.slice(0, 10).map(c => `· Folio ${c.folio} (${c.proveedor}) → OC ${c.numero_oc_sugerida} (${c.match_score}%)`).join('\n') +
        (candidatos.length > 10 ? `\n… y ${candidatos.length - 10} más` : '') +
        `\n\n¿Continuar?`
      );
      if (!ok) return;
      const res = await api.post('/compras/facturas-listas-contabilizar/asociar-masivo-best-match', {
        score_min: scoreMin, dry_run: false,
      });
      toast.success(`✓ ${res.data.asociadas} factura(s) asociadas · ${res.data.errores} error(es)`);
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error en asociación masiva');
    } finally {
      setAsociandoMasivo(false);
    }
  };

  // Contador de facturas con best_match ≥ 90 para mostrar en el botón
  const countBestMatch90 = (data?.facturas || []).filter(f => (f.best_match?.match_score || 0) >= 90).length
    + (data?.facturas_manuales || []).filter(f => (f.best_match?.match_score || 0) >= 90).length;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [res, descRes, lotesRes] = await Promise.all([
        api.get('/compras/facturas-listas-contabilizar'),
        api.get('/compras/facturas-contabilizar/descartadas'),
        api.get('/contabilidad/listas-contabilizar?estado=aprobada'),
      ]);
      setData(res.data);
      setDescartadas(descRes.data.descartadas || []);
      // Filter lotes that came from VB flow
      const lotes = (lotesRes.data.listas || []).filter(l =>
        l.observaciones && l.observaciones.includes('Lote VB')
      );
      setLotesVBPend(lotes);
      setSelected({});
    } catch { toast.error('Error cargando facturas'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Iter 419: clave compuesta (rut_proveedor, folio) para no mezclar facturas
  // de distintos proveedores que comparten el mismo folio.
  const fkey = (f) => `${(f.rut_proveedor || '').trim()}|${f.folio}`;

  const toggleSelect = (f) => {
    const k = fkey(f);
    setSelected(prev => {
      const next = { ...prev };
      if (next[k]) delete next[k]; else next[k] = f;
      return next;
    });
  };

  const selectAll = () => {
    const all = {};
    allPending.forEach(f => { all[fkey(f)] = f; });
    setSelected(all);
  };

  const allPending = [
    ...(data?.facturas_manuales || []).filter(f => !f.contabilizada || f.eliminada_alegra),
    ...(data?.facturas || []).filter(f => !f.contabilizada || f.eliminada_alegra),
  ];
  const selectedCount = Object.keys(selected).length;
  const selectedFacturas = allPending.filter(f => selected[fkey(f)]);
  const selectedMonto = selectedFacturas.reduce((s, f) => s + (f.neto || 0), 0);

  const handleContabilizar = async () => {
    if (selectedCount === 0) return;
    setContabilizando(true);
    try {
      const items = selectedFacturas.map(f => ({
        numero_oc: f.numero_oc || "",
        docmto: f.folio, dte_folio: f.folio,
        dte_rut_emisor: f.rut_proveedor,
        nombre_proveedor: f.proveedor,
        total_neto: f.neto, dte_total: f.total,
        numero_recepcion: f.numero_recepcion || "",
      }));
      const listaRes = await api.post('/contabilidad/listas-contabilizar', { items });
      await api.put(`/contabilidad/listas-contabilizar/${listaRes.data.lista_id}/aprobar`);
      const contabRes = await api.post(`/contabilidad/listas-contabilizar/${listaRes.data.lista_id}/contabilizar-alegra`);
      setResultModal({ open: true, data: contabRes.data });
      loadData();
    } catch (err) {
      // Iter 235.m.2 (Carlos): error visible y persistente — antes el toast se perdía
      // y solo quedaba el spinner girando.
      const detail = err.response?.data?.detail || err.response?.data?.message || err.message || 'Error contabilizando';
      toast.error(`No se pudo contabilizar: ${detail}`, {
        duration: 10000,  // 10 segundos
        description: err.response?.status ? `HTTP ${err.response.status}` : null,
      });
      // También dejamos rastro en consola para debug
      console.error('[handleContabilizar] error:', err.response?.status, err.response?.data || err.message);
    }
    finally { setContabilizando(false); }
  };

  // ────────────────── Cmd+K: comandos contextuales (Iter 235.h) ──────────────────
  useRegisterCmdKCommands(
    'facturas-contabilizar',
    'Facturas por Contabilizar',
    [
      // — Acciones bulk
      { id: 'fc-contabilizar-seleccionadas', label: `Contabilizar ${selectedCount} facturas seleccionadas en Alegra`,
        sublabel: `Total neto: $${(selectedMonto || 0).toLocaleString('es-CL')}`,
        icon: 'Send', keywords: 'contabilizar alegra seleccionadas lote bulk enviar',
        run: () => handleContabilizar(),
        enabled: selectedCount > 0 && !contabilizando },
      { id: 'fc-select-all', label: `Seleccionar todas las pendientes (${allPending.length})`,
        icon: 'CheckCircle', keywords: 'seleccionar todas pendientes contabilizar',
        run: () => selectAll(),
        enabled: allPending.length > 0 && selectedCount < allPending.length },
      { id: 'fc-clear-selection', label: 'Limpiar selección',
        sublabel: `${selectedCount} seleccionadas`,
        icon: 'X', keywords: 'limpiar deseleccionar reset',
        run: () => setSelected({}),
        enabled: selectedCount > 0 },
      // — Filtros estado
      { id: 'fc-filter-pendientes', label: 'Filtro: solo Pendientes (VB)',
        icon: 'Filter', keywords: 'filtro pendientes vb',
        run: () => setEstadoFilter('pendientes'),
        enabled: estadoFilter !== 'pendientes' },
      { id: 'fc-filter-contabilizadas', label: 'Filtro: solo Contabilizadas',
        icon: 'Filter', keywords: 'filtro contabilizadas alegra',
        run: () => setEstadoFilter('contabilizadas'),
        enabled: estadoFilter !== 'contabilizadas' },
      { id: 'fc-filter-todas', label: 'Filtro: Todas',
        icon: 'Filter', keywords: 'filtro todas limpiar',
        run: () => setEstadoFilter('todas'),
        enabled: estadoFilter !== 'todas' },
      // — Vistas
      { id: 'fc-toggle-descartadas', label: showDescartadas ? 'Ocultar descartadas' : `Mostrar descartadas (${descartadas.length})`,
        icon: 'Eye', keywords: 'descartadas eliminadas mostrar ocultar',
        run: () => setShowDescartadas((v) => !v),
        enabled: descartadas.length > 0 },
      // — Acciones genéricas
      { id: 'fc-refresh', label: 'Refrescar listado',
        icon: 'RefreshCw', keywords: 'refrescar recargar actualizar reload',
        run: () => loadData() },
      { id: 'fc-imprimir', label: 'Imprimir listado actual',
        icon: 'FileText', keywords: 'imprimir print pdf',
        run: () => imprimirFacturas() },
      { id: 'fc-export-excel', label: 'Exportar listado a Excel/CSV',
        icon: 'Download', keywords: 'exportar excel csv descargar',
        run: () => exportarFacturasExcel() },
    ],
    [selectedCount, selectedMonto, contabilizando, allPending.length, estadoFilter, showDescartadas, descartadas.length],
  );

  const handleContabilizarLote = async (listaId) => {
    setContabLote(listaId);
    try {
      const res = await api.post(`/contabilidad/listas-contabilizar/${listaId}/contabilizar-alegra`);
      setResultModal({ open: true, data: res.data });
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error contabilizando lote');
    } finally { setContabLote(null); }
  };

  // Issue #3: Batch clear selected
  const handleDescartarSeleccionadas = async () => {
    const items = selectedFacturas.map(f => ({ folio: f.folio, rut_proveedor: f.rut_proveedor || '' }));
    if (items.length === 0) return;
    try {
      await api.post('/compras/facturas-contabilizar/descartar-batch', items);
      toast.success(`${items.length} facturas descartadas`);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleDescartar = async (folio, rutProveedor = '') => {
    try {
      await api.post(`/compras/facturas-contabilizar/descartar/${folio}?rut_proveedor=${encodeURIComponent(rutProveedor || '')}`);
      toast.success(`Factura ${folio} descartada`);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleRestaurar = async (folio, rutProveedor = '') => {
    try {
      await api.delete(`/compras/facturas-contabilizar/descartar/${folio}?rut_proveedor=${encodeURIComponent(rutProveedor || '')}`);
      toast.success(`Factura ${folio} restaurada`);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleEliminarManual = async (folio) => {
    try {
      await api.delete(`/compras/facturas-contabilizar/manual/${folio}`);
      toast.success(`Factura ${folio} eliminada de lista manual`);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  // Limpiar TODAS las facturas pendientes
  const [confirmLimpiar, setConfirmLimpiar] = useState(false);
  const handleLimpiarTodas = async () => {
    if (!confirmLimpiar) {
      setConfirmLimpiar(true);
      return;
    }
    setConfirmLimpiar(false);
    try {
      const res = await api.post('/compras/facturas-contabilizar/limpiar-todas');
      toast.success(res.data.message);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al limpiar'); }
  };

  // Cargar listado sugerido (restaurar descartadas)
  const handleCargarSugeridas = async () => {
    try {
      const res = await api.post('/compras/facturas-contabilizar/cargar-sugeridas');
      toast.success(res.data.message);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al cargar sugeridas'); }
  };

  // Iter 235.c (Carlos): Re-verificar facturas marcadas "eliminada_alegra" contra API live
  const [verificandoEliminadas, setVerificandoEliminadas] = useState(false);
  const handleVerificarEliminadas = async () => {
    setVerificandoEliminadas(true);
    try {
      const res = await api.post('/compras/facturas-contabilizar/verificar-eliminadas-batch');
      const d = res.data;
      toast.success(
        `${d.verificadas} verificadas: ${d.confirmadas_existen} estaban en Alegra (corregidas), ${d.confirmadas_eliminadas} confirmadas eliminadas`,
        { duration: 6000 }
      );
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error verificando eliminadas');
    } finally {
      setVerificandoEliminadas(false);
    }
  };

  const handleBuscarFolio = async () => {
    if (!addFolio.trim()) return;
    setAdding(true);
    setSearchResult(null);
    try {
      const res = await api.get(`/compras/facturas-contabilizar/buscar-folio/${addFolio.trim()}`);
      setSearchResult(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Factura no encontrada'); }
    finally { setAdding(false); }
  };

  const handleConfirmarAgregar = async (darVb) => {
    if (!searchResult) return;
    setAdding(true);
    try {
      const res = await api.post(`/compras/facturas-contabilizar/agregar-por-folio?folio=${searchResult.folio}&dar_vb=${darVb}&rut_emisor=${encodeURIComponent(searchResult.rut_proveedor || '')}`);
      toast.success(res.data.message);
      setAddFolio('');
      setSearchResult(null);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setAdding(false); }
  };

  // Load OTs for dropdown
  const loadOts = async (filtro) => {
    try {
      const res = await api.get(`/compras/ots-dropdown?filtro=${filtro}`);
      setOtList(res.data.ots || []);
    } catch { setOtList([]); }
  };

  // Issue #4: Express OC from search AND from row - now loads preview
  const openExpressModal = async (folio, opts = {}) => {
    const reemplazarOc = !!opts.reemplazarOc;
    const ocAnterior = opts.ocAnterior || null;
    const rutEmisor = opts.rutEmisor || null;
    setExpressModal({ open: true, folio, rut_emisor: rutEmisor, reemplazar_oc_actual: reemplazarOc, oc_anterior: ocAnterior });
    setExpressOT('');
    setExpressSolicitante(authUser?.nombre || authUser?.email || ''); // Iter 389
    setPreviewData(null);
    setItemsCtas({});
    setItemsOts({});
    setPreviewLoading(true);
    setOtFiltro('ejecucion');
    loadOts('ejecucion');
    try {
      const res = await api.get(`/contabilidad/preview-contabilizacion/${folio}${opts.rutEmisor ? `?rut_emisor=${encodeURIComponent(opts.rutEmisor)}` : ''}`);
      setPreviewData(res.data);
      const suggested = {};
      (res.data.items || []).forEach(it => {
        if (it.cta_sugerida) suggested[it.linea] = it.cta_sugerida;
      });
      setItemsCtas(suggested);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error cargando preview');
    } finally { setPreviewLoading(false); }
  };

  const handleExpressOC = async () => {
    if (!expressModal.folio) return;
    // Iter 389 (Renzo) — OT (general o por ítem) y Solicitante obligatorios; el backend valida igual (validador único).
    const defaultOt = parseInt(expressOT) || 0;
    const itemsSinOt = (previewData?.items || []).filter((item) => !(parseInt(itemsOts[item.linea]) || defaultOt));
    if (!defaultOt && (itemsSinOt.length > 0 || !(previewData?.items || []).length)) {
      toast.error('⛔ La OT es obligatoria: selecciona la OT por defecto o una OT en cada ítem');
      return;
    }
    if (!expressSolicitante || !expressSolicitante.trim()) { toast.error('⛔ El Solicitante es obligatorio para grabar la OC Express'); return; }
    if (!expressCondMP.condicion_codigo) { toast.error('Debes seleccionar una Condición de Pago vigente'); return; }
    if (expressCondMP.requiere_medio_pago && !expressCondMP.medio_pago_codigo) {
      toast.error(`La condición "${expressCondMP.condicion?.nombre}" requiere un Medio de Pago`);
      return;
    }
    setExpressProcesando(true);
    try {
      const items_ctas_list = (previewData?.items || []).map((item) => ({
        linea: item.linea,
        ctagasto: itemsCtas[item.linea] || '',
        ot_numero: parseInt(itemsOts[item.linea]) || defaultOt,
      }));
      const res = await api.post('/compras/facturas-contabilizar/oc-express', {
        folio: expressModal.folio,
        rut_emisor: expressModal.rut_emisor || '',
        ot_numero: defaultOt,
        solicitante: expressSolicitante.trim(), // Iter 389
        items_ctas: items_ctas_list,
        // Iter 365: par canónico del catálogo (el backend rechaza condiciones no vigentes)
        condicion_pago_codigo: expressCondMP.condicion_codigo,
        medio_pago_codigo: expressCondMP.requiere_medio_pago ? expressCondMP.medio_pago_codigo : '',
        // Iter 235.i (Carlos): si la OC asociada no corresponde, desasocia antes de crear Express
        reemplazar_oc_actual: !!expressModal.reemplazar_oc_actual,
      });
      toast.success(expressModal.reemplazar_oc_actual
        ? `OC anterior ${expressModal.oc_anterior || ''} desasociada · ${res.data.message}`
        : res.data.message);
      setExpressModal({ open: false, folio: null, reemplazar_oc_actual: false, oc_anterior: null });
      setPreviewData(null);
      setSearchResult(null);
      setAddFolio('');
      setExpressCondMP(EXPRESS_CMP_VACIO);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al crear OC Express'); }
    finally { setExpressProcesando(false); }
  };

  const handleSoloVbAgregar = async () => {
    const folio = expressModal.folio;
    const rutEmisorVb = expressModal.rut_emisor || '';
    setExpressModal({ open: false, folio: null, rut_emisor: null, reemplazar_oc_actual: false, oc_anterior: null });
    if (!folio) return;
    setAdding(true);
    try {
      const res = await api.post(`/compras/facturas-contabilizar/agregar-por-folio?folio=${folio}&dar_vb=true&rut_emisor=${encodeURIComponent(rutEmisorVb)}`);
      toast.success(res.data.message);
      setSearchResult(null);
      setAddFolio('');
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setAdding(false); }
  };

  // Issue #2: View OC in modal - with not-found handling
  const handleViewOC = async (numeroOc, folio, rutEmisor = '') => {
    if (!numeroOc) return;
    setOcModal({ open: true, data: null, loading: true, notFound: false, ocNumero: numeroOc, folio, rutEmisor });
    try {
      const res = await api.get(`/compras/orden-compra/${numeroOc}/detalle-completo`);
      setOcModal({ open: true, data: res.data, loading: false, notFound: false, ocNumero: numeroOc, folio, rutEmisor });
    } catch (err) {
      if (err.response?.status === 404) {
        setOcModal({ open: true, data: null, loading: false, notFound: true, ocNumero: numeroOc, folio, rutEmisor });
      } else {
        toast.error('Error al cargar OC');
        setOcModal(prev => ({ ...prev, loading: false }));
      }
    }
  };

  // Rechazar factura porque OC no existe
  const handleRechazarPorNoOC = async (folio, ocNumero, rutEmisor = '') => {
    try {
      await api.post(`/compras/facturas-contabilizar/descartar/${folio}?rut_proveedor=${encodeURIComponent(rutEmisor || '')}`);
      toast.success(`Factura ${folio} descartada - OC ${ocNumero} no existe`);
      setOcModal({ open: false, data: null, loading: false, notFound: false, ocNumero: null, folio: null });
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al descartar'); }
  };

  // OC Express desde modal de OC no encontrada
  const handleExpressFromOCModal = (folio) => {
    setOcModal({ open: false, data: null, loading: false, notFound: false, ocNumero: null, folio: null });
    openExpressModal(folio);
  };

  // View DTE in modal
  const handleViewDTE = async (folio) => {
    setDteModal({ open: true, folio, data: null, operaciones: [], loading: true });
    try {
      const res = await api.get(`/contabilidad/acepta-rechazo-dte/detalle/${folio}`);
      setDteModal({ open: true, folio, data: res.data.dte, operaciones: res.data.operaciones || [], loading: false });
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error(`DTE con folio ${folio} no encontrado`);
      } else {
        toast.error('Error al cargar DTE');
      }
      setDteModal(prev => ({ ...prev, loading: false }));
    }
  };

  // Issue #1: Recepcionar - handle missing reception gracefully (PDF in modal, no popup)
  const handleViewRecepcion = async (numRecep) => {
    if (!numRecep) {
      toast.error('No existe recepcion para esta factura');
      return;
    }
    setPdfModal({ open: true, url: null, title: `Recepcion ${numRecep}`, loading: true });
    const blobUrl = await fetchAuthPDF(`/bodega/recepcion/${numRecep}/pdf`);
    if (blobUrl) {
      setPdfModal({ open: true, url: blobUrl, title: `Recepcion ${numRecep}`, loading: false });
    } else {
      setPdfModal({ open: false, url: null, title: '', loading: false });
    }
  };

  const handleRecepcionarDTE = async (f) => {
    setRecepcionando(f.folio);
    try {
      const res = await api.post('/bodega/recepcionar-desde-dte', {
        folio: f.folio,
        numero_oc: f.numero_oc || '',
      });
      toast.success(res.data.message);
      if (res.data.pdf_url) {
        setPdfModal({ open: true, url: null, title: `Recepcion ${f.folio}`, loading: true });
        const blobUrl = await fetchAuthPDF(res.data.pdf_url);
        if (blobUrl) {
          setPdfModal({ open: true, url: blobUrl, title: `Recepcion ${f.folio}`, loading: false });
        } else {
          setPdfModal({ open: false, url: null, title: '', loading: false });
        }
      }
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al recepcionar'); }
    finally { setRecepcionando(null); }
  };

  const filtered = (data?.facturas || []).filter(f => {
    // Filtro por estado: 'pendientes' (default) | 'contabilizadas' | 'todas'
    if (estadoFilter === 'pendientes' && f.contabilizada && !f.eliminada_alegra) return false;
    if (estadoFilter === 'contabilizadas' && !f.contabilizada) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return f.folio?.includes(s) || f.proveedor?.toLowerCase().includes(s) ||
      f.numero_oc?.includes(s) || f.rut_proveedor?.includes(s);
  });

  const manualesFiltered = (data?.facturas_manuales || []).filter(f => {
    if (estadoFilter === 'pendientes' && f.contabilizada && !f.eliminada_alegra) return false;
    if (estadoFilter === 'contabilizadas' && !f.contabilizada) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return f.folio?.includes(s) || f.proveedor?.toLowerCase().includes(s) ||
      f.numero_oc?.includes(s) || f.rut_proveedor?.includes(s);
  });

  const renderRow = (f, i, isManual) => {
    const isContab = f.contabilizada;
    const isEliminada = f.eliminada_alegra;
    const isSelected = selected[fkey(f)];
    const prefix = isManual ? 'manual' : 'auto';
    const rowBg = isEliminada ? 'bg-orange-50/40'
      : isContab ? 'bg-green-50/40'
      : f.validacion_oc?.excede_monto ? 'bg-red-50/60'
      : f.validacion_oc?.proveedor_diferente ? 'bg-blue-50/40'
      : isSelected ? (isManual ? 'bg-indigo-50/60' : 'bg-teal-50/60')
      : 'hover:bg-gray-50';

    return (
      <tr key={`${prefix}-${f.rut_proveedor || ''}-${f.folio}-${i}`}
        className={`border-t transition-colors ${rowBg}`}
        data-testid={`${prefix}-row-${f.rut_proveedor || 'srut'}-${f.folio}`}>
        <td className="px-2 py-1.5 text-center">
          {(!isContab || isEliminada) ? (
            <button onClick={() => toggleSelect(f)}
              className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                isSelected ? 'bg-teal-500 border-teal-500 text-white' : 'border-gray-300 hover:border-teal-400'
              }`} data-testid={`check-${prefix}-${f.rut_proveedor || 'srut'}-${f.folio}`}>
              {isSelected && <Check className="w-3.5 h-3.5" />}
            </button>
          ) : <span className={isEliminada ? "text-orange-500" : "text-green-500"}><CheckCircle className="w-4 h-4" /></span>}
        </td>
        <td className={`px-3 py-1.5 font-mono font-bold ${isManual ? 'text-indigo-700' : 'text-teal-700'}`}>
          <div className="flex items-center gap-1">
            <span>{f.folio}</span>
            {/* Iter 235.n (Carlos): badge RCV cuando la factura proviene del RCV
                provisional del SII (sin XML real). Igual lógica que en AR. */}
            {f.origen_dte === 'rcv_provisional' && !f.tiene_xml && (
              <span
                className="inline-flex items-center px-1 py-0.5 rounded text-[9px] font-medium bg-amber-100 text-amber-800 border border-amber-300"
                title={`Sin XML: información provisional del RCV SII${f.rcv_periodo ? ' · período ' + f.rcv_periodo : ''}. El proveedor aún no envió el DTE formal.`}
                data-testid={`badge-rcv-${f.folio}`}
              >
                📋 RCV
              </span>
            )}
          </div>
        </td>
        <td className="px-3 py-1.5 truncate max-w-[180px]">
          {f.proveedor}
          <span className="block text-[10px] text-gray-400">{f.rut_proveedor}</span>
        </td>
        <td className="px-3 py-1.5">
          {f.numero_oc && !f.oc_no_encontrada ? (
            (f.oc_es_express || f.numero_oc.startsWith?.('W')) ? (
              <span className="inline-flex items-center gap-0.5 bg-amber-50 text-amber-700 px-2 py-0.5 rounded text-[10px] font-medium" title={`OC Express ${f.numero_oc}`}>
                <Zap className="w-3 h-3" /> {f.numero_oc}
              </span>
            ) : (
              <div className="inline-flex items-center gap-1 group/oc">
                <span className="inline-flex items-center gap-0.5 bg-green-100 text-green-700 px-2 py-0.5 rounded text-[10px] font-medium">
                  <Package className="w-3 h-3" /> {f.numero_oc}
                </span>
                {/* Iter 235.i (Carlos): el proveedor escribió un # de OC que existe pero no
                    corresponde. Aparece SOLO al hover, en gris discreto para no confundir
                    con un símbolo de error. */}
                <button
                  onClick={() => {
                    const ok = window.confirm(
                      `¿La OC ${f.numero_oc} NO corresponde a esta factura?\n\n` +
                      `Se desasociará y se creará una OC Express en su lugar.\n` +
                      `Quedará registro en el DTE para auditoría.`
                    );
                    if (ok) openExpressModal(f.folio, { reemplazarOc: true, ocAnterior: f.numero_oc, rutEmisor: f.rut_proveedor });
                  }}
                  className="inline-flex items-center justify-center w-4 h-4 rounded text-gray-300 hover:text-orange-600 opacity-0 group-hover/oc:opacity-100 transition-opacity"
                  title={`La OC ${f.numero_oc} no corresponde — crear OC Express en su lugar`}
                  data-testid={`oc-no-corresponde-${f.folio}`}
                >
                  <MoreHorizontal className="w-3 h-3" />
                </button>
              </div>
            )
          ) : (
            <div className="flex items-center gap-1 flex-wrap">
              {/* Iter 308.a (Renzo, ago-2026): el campo `orden_compra` del XML cuando
                  el RUT del DTE no coincide (o el número no existe) es basura del
                  proveedor (RUT nuestro, folio ajeno, etc.). Ya no lo mostramos —
                  la OC real se define asociando o creando OC Express, y el backend
                  sobreescribe `orden_compra` con el número correcto. */}
              {/* Iter 235.j (Carlos): badge prominente para best_match (score ≥ 70).
                  Un click asocia. Match exacto monto+fecha → casi siempre correcto. */}
              {f.best_match && (
                <button
                  onClick={() => handleAsociarOC(f.folio, f.rut_proveedor, f.best_match.numero_oc)}
                  disabled={asociandoOc}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                    f.best_match.match_score >= 90
                      ? 'bg-emerald-500 text-white hover:bg-emerald-600 shadow-sm ring-1 ring-emerald-300 animate-pulse'
                      : 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200 border border-emerald-400'
                  }`}
                  title={`Match ${f.best_match.match_score}% · OC ${f.best_match.numero_oc} · Disponible $${(f.best_match.disponible || 0).toLocaleString('es-CL')} · Fecha ${f.best_match.fecha} · Click para asociar`}
                  data-testid={`best-match-${f.folio}`}
                >
                  <span>🎯</span>
                  <span>OC {f.best_match.numero_oc}</span>
                  <span className="opacity-80">· {f.best_match.match_score}%</span>
                </button>
              )}
              {f.ocs_disponibles?.length > 0 && !f.best_match && (
                <span
                  className="inline-flex items-center gap-0.5 bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-[10px] font-medium"
                  title={`${f.ocs_disponibles.length} OC(s) disponible(s) del proveedor — ver detalle en AR`}
                >
                  <Package className="w-3 h-3" /> {f.ocs_disponibles.length} disp.
                </span>
              )}
              <button onClick={() => openExpressModal(f.folio, { rutEmisor: f.rut_proveedor })}
                className="inline-flex items-center gap-0.5 bg-amber-50 text-amber-600 px-2 py-0.5 rounded text-[10px] font-medium hover:bg-amber-100 transition-colors"
                title="Crear OC Express" data-testid={`express-row-${f.folio}`}>
                <Zap className="w-3 h-3" /> OC Express
              </button>
            </div>
          )}
        </td>
        <td className="px-3 py-1.5 text-gray-600">{fmtFecha(f.fecha_emision)}</td>
        <td className="px-3 py-1.5 text-right font-mono">
          ${fmt(f.neto)}
          <MontoAlert validacion={f.validacion_oc} />
        </td>
        <td className="px-3 py-1.5 text-right font-mono text-gray-600">${fmt(f.iva || Math.round((f.neto || 0) * 0.19))}</td>
        <td className="px-2 py-1.5 text-right font-mono">
          {(f.impuestos_adicionales || []).reduce((s, x) => s + (x.monto || 0), 0) > 0 ? (
            <span className="text-orange-600 text-[10px] font-medium" title={(f.impuestos_adicionales || []).map(x => `${x.nombre || 'Imp.'} ${x.tasa || 0}%: $${fmt(x.monto)}`).join(', ')}>
              ${fmt((f.impuestos_adicionales || []).reduce((s, x) => s + (x.monto || 0), 0))}
            </span>
          ) : <span className="text-gray-300">-</span>}
        </td>
        <td className="px-3 py-1.5 text-right font-mono font-medium">${fmt(f.total)}</td>
        <td className="px-3 py-1.5 text-center">
          <ContabBadge
            contabilizada={isContab}
            eliminadaAlegra={isEliminada}
            alegra_id={f.alegra_id || f.alegra_bill_id || ''}
            estado_pago={f.estado_pago || ''}
            en_nomina={f.en_nomina}
            liberada_para_pago={f.liberada_para_pago}
          />
        </td>
        {/* Forma de Pago */}
        <td className="px-2 py-1.5 text-center" data-testid={`fpago-${f.folio}`}>
          {(f.condicion_pago_oc || f.medio_pago_dte) ? (
            <div className="space-y-0.5">
              {f.condicion_pago_oc && (
                <span className={`block text-[9px] font-medium truncate max-w-[80px] mx-auto ${
                  f.condicion_pago_oc.startsWith('TC') || f.condicion_pago_oc === 'CMR'
                    ? 'text-orange-600' : 'text-gray-600'
                }`} title={`OC: ${f.condicion_pago_oc}`}>
                  {f.condicion_pago_oc}
                </span>
              )}
              {f.medio_pago_dte && (
                <span className={`block text-[9px] truncate max-w-[80px] mx-auto ${
                  (f.medio_pago_dte || '').toLowerCase().includes('tarjeta')
                    ? 'text-orange-500 font-medium' : 'text-blue-500'
                }`} title={`DTE: ${f.medio_pago_dte}`}>
                  {f.medio_pago_dte}
                </span>
              )}
            </div>
          ) : <span className="text-gray-300 text-[9px]">-</span>}
        </td>
        {/* Factura DTE - opens modal */}
        <td className="px-1 py-1.5 text-center">
          {f.tiene_dte ? (
            <button onClick={() => handleViewDTE(f.folio)}
              className="p-1 rounded hover:bg-blue-50 text-blue-600 hover:text-blue-800"
              title="Ver Factura DTE" data-testid={`doc-dte-${f.folio}`}>
              <FileText className="w-3.5 h-3.5" />
            </button>
          ) : <span className="text-gray-200">-</span>}
        </td>
        {/* OC - opens modal */}
        <td className="px-1 py-1.5 text-center">
          {f.numero_oc ? (
            <button onClick={() => handleViewOC(f.numero_oc, f.folio, f.rut_proveedor)}
              className="p-1 rounded hover:bg-green-50 text-green-600 hover:text-green-800"
              title={`Ver OC ${f.numero_oc}`} data-testid={`doc-oc-${f.folio}`}>
              <ClipboardList className="w-3.5 h-3.5" />
            </button>
          ) : <span className="text-gray-200">-</span>}
        </td>
        {/* Recepcion */}
        <td className="px-1 py-1.5 text-center">
          {f.numero_recepcion ? (
            <div className="flex items-center gap-0.5 justify-center">
              <button onClick={() => handleViewRecepcion(f.numero_recepcion)}
                className="p-1 rounded hover:bg-purple-50 text-purple-600 hover:text-purple-800"
                title={`Ver Recepcion ${f.numero_recepcion}`} data-testid={`doc-recep-${f.folio}`}>
                <ScanLine className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <button onClick={() => handleRecepcionarDTE(f)}
              disabled={recepcionando === f.folio}
              className="p-1 rounded hover:bg-orange-50 text-orange-500 hover:text-orange-700 disabled:opacity-50"
              title="Recepcionar desde DTE" data-testid={`recepcionar-${f.folio}`}>
              {recepcionando === f.folio
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <PackagePlus className="w-3.5 h-3.5" />}
            </button>
          )}
        </td>
        <td className="px-2 py-1.5 text-center">
          {isManual ? (
            <button onClick={() => handleEliminarManual(f.folio)}
              className="text-gray-400 hover:text-red-600" title="Eliminar de lista manual"
              data-testid={`del-manual-${f.folio}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          ) : !isContab ? (
            <button onClick={() => handleDescartar(f.folio, f.rut_proveedor)}
              className="text-gray-400 hover:text-red-600" title="Descartar"
              data-testid={`discard-${f.rut_proveedor || 'srut'}-${f.folio}`}>
              <X className="w-3.5 h-3.5" />
            </button>
          ) : null}
        </td>
      </tr>
    );
  };

  const tableHeaders = (bgClass) => (
    <tr className={`${bgClass} text-white text-[10px]`}>
      <th className="px-2 py-2 w-8 text-center"><Check className="w-3.5 h-3.5 mx-auto" /></th>
      <th className="px-3 py-2 text-left">N Factura</th>
      <th className="px-3 py-2 text-left">Proveedor</th>
      <th className="px-3 py-2 text-left">OC</th>
      <th className="px-3 py-2 text-left">Fecha</th>
      <th className="px-3 py-2 text-right">Neto</th>
      <th className="px-3 py-2 text-right">IVA</th>
      <th className="px-2 py-2 text-right text-[10px]">Otros Imp.</th>
      <th className="px-3 py-2 text-right">Total</th>
      <th className="px-3 py-2 text-center">Estado</th>
      <th className="px-2 py-2 text-center text-[10px]" title="Forma de Pago">F. Pago</th>
      <th className="px-1 py-2 text-center" title="Ver Factura DTE"><FileText className="w-3.5 h-3.5 mx-auto" /><span className="block text-[8px]">Factura</span></th>
      <th className="px-1 py-2 text-center" title="Ver Orden de Compra"><ClipboardList className="w-3.5 h-3.5 mx-auto" /><span className="block text-[8px]">OC</span></th>
      <th className="px-1 py-2 text-center" title="Recepcion / Scan"><ScanLine className="w-3.5 h-3.5 mx-auto" /><span className="block text-[8px]">Recep.</span></th>
      <th className="px-2 py-2 w-8"></th>
    </tr>
  );

  const pendingAutoCount = filtered.filter(f => !f.contabilizada && !f.eliminada_alegra).length;
  const contabAutoCount = filtered.filter(f => f.contabilizada && !f.eliminada_alegra).length;
  const eliminadaCount = filtered.filter(f => f.eliminada_alegra).length;
  const excedeCount = filtered.filter(f => f.validacion_oc?.excede_monto).length;

  // Carlos (09/05/2026): imprimir y exportar listado de facturas
  const buildFacturasList = () => filtered;

  const imprimirFacturas = () => {
    const facts = buildFacturasList();
    if (!facts.length) {
      toast.warning('No hay facturas para imprimir con los filtros actuales');
      return;
    }
    printTable({
      title: 'Facturas Listas para Contabilizar',
      subtitle: `${facts.length} facturas · Generado ${new Date().toLocaleString('es-CL')}`,
      summary: [
        { label: 'Pendientes', value: pendingAutoCount },
        { label: 'Contabilizadas', value: contabAutoCount, color: '#047857' },
        { label: 'Excede monto OC', value: excedeCount, color: '#b91c1c' },
        { label: 'Monto neto pend.', value: `$${fmt(data?.monto_total)}`, color: '#0f766e' },
      ],
      columns: [
        { key: 'numero_ref', label: 'N. Factura', mono: true, format: (v, r) => v || r.docmto || '' },
        { key: 'rut_proveedor', label: 'RUT', mono: true },
        { key: 'razon_social', label: 'Proveedor', format: (v, r) => v || r.nombre_proveedor || '' },
        { key: 'numero_oc', label: 'OC', mono: true, align: 'center' },
        { key: 'fecha_emision', label: 'Fecha', format: (v) => fmtFecha(v) || '-' },
        { key: 'total_neto', label: 'Neto', align: 'right', mono: true, format: (v) => `$${fmt(v)}` },
        { key: 'iva', label: 'IVA', align: 'right', mono: true, format: (v) => `$${fmt(v)}` },
        { key: 'total', label: 'Total', align: 'right', mono: true, format: (v) => `$${fmt(v)}` },
        { key: 'forma_pago', label: 'F. Pago' },
        { key: 'contabilizada', label: 'Estado', align: 'center', format: (v, r) => r.eliminada_alegra ? 'ELIMINADA' : (v ? 'CONTABILIZADA' : 'PEND.') },
      ],
      rows: facts,
    });
  };

  const exportarFacturasExcel = () => {
    const facts = buildFacturasList();
    if (!facts.length) {
      toast.warning('No hay facturas para exportar');
      return;
    }
    exportToCSV({
      filename: 'facturas_contabilizar',
      title: 'CHIRINO STEEL · Facturas Listas para Contabilizar',
      summary: [
        { label: 'Total facturas', value: facts.length },
        { label: 'Pendientes', value: pendingAutoCount },
        { label: 'Contabilizadas', value: contabAutoCount },
        { label: 'Excede monto OC', value: excedeCount },
        { label: 'Monto neto pendiente (CLP)', value: Math.round(data?.monto_total || 0) },
        { label: 'Generado', value: new Date().toLocaleString('es-CL') },
      ],
      columns: [
        { key: 'numero_ref', label: 'N. Factura', format: (v, r) => v || r.docmto || '' },
        { key: 'tipo_dte', label: 'Tipo DTE' },
        { key: 'rut_proveedor', label: 'RUT' },
        { key: 'razon_social', label: 'Proveedor', format: (v, r) => v || r.nombre_proveedor || '' },
        { key: 'numero_oc', label: 'N. OC' },
        { key: 'estado_recepcion', label: 'Recepción' },
        { key: 'fecha_emision', label: 'Fecha emisión', format: (v) => fmtFecha(v) || '' },
        { key: 'fecha_vencimiento', label: 'Fecha vencimiento', format: (v) => fmtFecha(v) || '' },
        { key: 'total_neto', label: 'Neto (CLP)', format: (v) => Math.round(Number(v) || 0) },
        { key: 'iva', label: 'IVA (CLP)', format: (v) => Math.round(Number(v) || 0) },
        { key: 'otros_impuestos', label: 'Otros Imp. (CLP)', format: (v) => Math.round(Number(v) || 0) },
        { key: 'total', label: 'Total (CLP)', format: (v) => Math.round(Number(v) || 0) },
        { key: 'forma_pago', label: 'Forma Pago' },
        { key: 'contabilizada', label: 'Contabilizada', format: (v) => v ? 'SÍ' : 'NO' },
        { key: 'eliminada_alegra', label: 'Eliminada Alegra', format: (v) => v ? 'SÍ' : '' },
        { key: 'observaciones', label: 'Observaciones' },
      ],
      rows: facts,
    });
  };

  return (
    <div className="space-y-5" data-testid="facturas-contabilizar-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <DollarSign className="w-6 h-6 text-teal-600" />
          Facturas Listas para Contabilizar
        </h1>
        <p className="text-sm text-muted-foreground">
          Facturas con VB Gerencia pendientes de contabilizar en Alegra.
        </p>
      </div>

      {data && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="bg-white border rounded p-3 text-center">
            <p className="text-xs text-muted-foreground">Pendientes</p>
            <p className="text-xl font-bold">{pendingAutoCount}</p>
          </div>
          <div className="bg-teal-50 border border-teal-200 rounded p-3 text-center">
            <p className="text-xs text-teal-700">Monto Neto Pend.</p>
            <p className="text-lg font-bold text-teal-700">${fmt(data.monto_total)}</p>
          </div>
          <div className="bg-indigo-50 border border-indigo-200 rounded p-3 text-center">
            <p className="text-xs text-indigo-700">Agregadas Manual</p>
            <p className="text-xl font-bold text-indigo-700">{data.total_manuales}</p>
          </div>
          <div className="bg-green-50 border border-green-200 rounded p-3 text-center">
            <p className="text-xs text-green-700">Contabilizadas</p>
            <p className="text-xl font-bold text-green-700">{contabAutoCount}</p>
          </div>
          {excedeCount > 0 ? (
            <div className="bg-red-50 border border-red-200 rounded p-3 text-center">
              <p className="text-xs text-red-700">Exceden Monto OC</p>
              <p className="text-xl font-bold text-red-700">{excedeCount}</p>
            </div>
          ) : (
            <div className="bg-white border rounded p-3 text-center">
              <p className="text-xs text-muted-foreground">Seleccionadas</p>
              <p className="text-xl font-bold text-teal-600">{selectedCount}</p>
            </div>
          )}
        </div>
      )}

      {/* Toolbar */}
      <div className="bg-white border rounded-lg p-4 space-y-3">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 max-w-sm">
            <label className="block text-xs font-medium mb-1">Buscar en listado</label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input placeholder="Folio, proveedor, OC..." value={search}
                onChange={(e) => setSearch(e.target.value)} className="pl-9"
                data-testid="search-filter-input" />
            </div>
          </div>

          {/* Filtro por estado de contabilización */}
          <div>
            <label className="block text-xs font-medium mb-1">Mostrar</label>
            <div className="inline-flex border rounded-md overflow-hidden text-xs" data-testid="estado-filter-group">
              <button
                type="button"
                onClick={() => setEstadoFilter('pendientes')}
                className={`px-3 py-1.5 border-r font-medium transition ${
                  estadoFilter === 'pendientes'
                    ? 'bg-amber-500 text-white'
                    : 'bg-white text-gray-700 hover:bg-amber-50'
                }`}
                data-testid="filter-pendientes"
                title="Facturas con VB dado pendientes de contabilizar (default)"
              >
                🟡 VB para contabilizar
              </button>
              <button
                type="button"
                onClick={() => setEstadoFilter('contabilizadas')}
                className={`px-3 py-1.5 border-r font-medium transition ${
                  estadoFilter === 'contabilizadas'
                    ? 'bg-emerald-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-emerald-50'
                }`}
                data-testid="filter-contabilizadas"
              >
                ✅ Contabilizadas
              </button>
              <button
                type="button"
                onClick={() => setEstadoFilter('todas')}
                className={`px-3 py-1.5 font-medium transition ${
                  estadoFilter === 'todas'
                    ? 'bg-gray-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
                data-testid="filter-todas"
              >
                📋 Todas
              </button>
            </div>
          </div>

          <Button variant="outline" size="sm" onClick={selectAll} data-testid="select-all-btn">Seleccionar todo</Button>
          {selectedCount > 0 && (
            <>
              <Button variant="outline" size="sm" onClick={() => setSelected({})}>Limpiar sel.</Button>
              <Button variant="destructive" size="sm" onClick={handleDescartarSeleccionadas}
                data-testid="descartar-batch-btn">
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Descartar ({selectedCount})
              </Button>
            </>
          )}
          <Button variant="outline" onClick={loadData} disabled={loading} data-testid="refresh-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button variant="outline" size="sm" onClick={imprimirFacturas} title="Imprimir listado" data-testid="btn-print-facturas">
            <Printer className="w-3.5 h-3.5 mr-1" /> Imprimir
          </Button>
          <Button variant="outline" size="sm" onClick={exportarFacturasExcel} className="text-emerald-700 border-emerald-300 hover:bg-emerald-50" title="Exportar a Excel/CSV" data-testid="btn-excel-facturas">
            <FileSpreadsheet className="w-3.5 h-3.5 mr-1" /> Excel
          </Button>
          <Button variant={confirmLimpiar ? "default" : "destructive"} size="sm" onClick={handleLimpiarTodas}
            data-testid="limpiar-todas-btn"
            className={confirmLimpiar ? "bg-amber-600 hover:bg-amber-700 animate-pulse" : ""}>
            <Trash2 className="w-3.5 h-3.5 mr-1" /> {confirmLimpiar ? "Confirmar Limpiar" : "Limpiar Todas"}
          </Button>
          {confirmLimpiar && (
            <Button variant="outline" size="sm" onClick={() => setConfirmLimpiar(false)}>Cancelar</Button>
          )}
          <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={handleCargarSugeridas}
            data-testid="cargar-sugeridas-btn" title="Restaurar listado sugerido (facturas con VB + DTEs aceptados)">
            <ClipboardList className="w-3.5 h-3.5 mr-1" /> Cargar Sugeridas
          </Button>
          {/* Iter 235.c (Carlos): re-verificar contra API live de Alegra para limpiar falsos positivos */}
          <Button size="sm" variant="outline" onClick={handleVerificarEliminadas}
            disabled={verificandoEliminadas}
            data-testid="verificar-eliminadas-btn"
            title="Verifica contra la API live de Alegra si las facturas marcadas 'Eliminada' realmente lo están. Útil cuando aparecen facturas como pendientes que ya están en Alegra.">
            {verificandoEliminadas
              ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
              : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
            Re-verificar Alegra
          </Button>
          {/* Iter 235.l (Carlos): Capa 3 — asociación masiva por best_match */}
          {countBestMatch90 > 0 && (
            <Button
              onClick={() => handleAsociarMasivo(90)}
              disabled={asociandoMasivo}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="asociar-masivo-btn"
            >
              {asociandoMasivo ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <span className="mr-2">🎯</span>
              )}
              Asociar {countBestMatch90} match exactos (≥90%)
            </Button>
          )}
          {selectedCount > 0 && (
            <Button onClick={handleContabilizar} disabled={contabilizando} className="bg-teal-600 hover:bg-teal-700"
              data-testid="contabilizar-btn">
              {contabilizando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Contabilizar ({selectedCount})
            </Button>
          )}
        </div>

        {/* Search folio to add */}
        <div className="border-t pt-3 space-y-3">
          <div className="flex gap-2 items-end">
            <div className="flex-1 max-w-xs">
              <label className="block text-xs font-medium mb-1">Buscar factura por N Folio</label>
              <Input placeholder="Ej: 6411962" value={addFolio}
                onChange={(e) => { setAddFolio(e.target.value); setSearchResult(null); }}
                onKeyDown={(e) => e.key === 'Enter' && handleBuscarFolio()}
                data-testid="add-folio-input" />
            </div>
            <Button onClick={handleBuscarFolio} disabled={adding} variant="outline" data-testid="add-folio-btn">
              {adding ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Search className="w-4 h-4 mr-1" />}
              Buscar
            </Button>
            {descartadas.length > 0 && (
              <Button variant="ghost" size="sm" onClick={() => setShowDescartadas(!showDescartadas)}
                className="text-gray-500 text-xs" data-testid="descartadas-btn">
                Descartadas ({descartadas.length})
              </Button>
            )}
          </div>

          {searchResult && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3" data-testid="search-result-card">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div>
                  <p className="text-xs text-gray-500">Folio</p>
                  <p className="font-bold text-blue-700">{searchResult.folio}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Proveedor</p>
                  <p className="font-medium">{searchResult.proveedor}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Montos</p>
                  <p className="text-xs">Neto: ${fmt(searchResult.neto)} | IVA: ${fmt(searchResult.iva)}</p>
                  <p className="font-bold">Total: ${fmt(searchResult.total)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">OC</p>
                  <p className="font-medium">{searchResult.numero_oc || 'Sin OC'}</p>
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                {searchResult.tiene_vb ? (
                  <Button size="sm" onClick={() => handleConfirmarAgregar(false)} className="bg-teal-600 hover:bg-teal-700"
                    data-testid="agregar-btn">
                    <Plus className="w-4 h-4 mr-1" /> Agregar
                  </Button>
                ) : (
                  <>
                    {/* OC Express solo aplica si la factura NO tiene OC asociada */}
                    {!searchResult.numero_oc && (
                      <Button size="sm" onClick={() => openExpressModal(searchResult.folio, { rutEmisor: searchResult.rut_proveedor })}
                        className="bg-amber-500 hover:bg-amber-600" data-testid="dar-vb-agregar-btn">
                        <Zap className="w-4 h-4 mr-1" /> OC Express + Recepcion
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => handleConfirmarAgregar(true)}
                      data-testid="solo-vb-btn-search">
                      <ShieldCheck className="w-4 h-4 mr-1" /> Solo Dar VB
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleConfirmarAgregar(false)}
                      data-testid="agregar-sin-vb-btn">
                      <Plus className="w-4 h-4 mr-1" /> Agregar sin VB
                    </Button>
                  </>
                )}
                <Button size="sm" variant="ghost" onClick={() => setSearchResult(null)}>Cancelar</Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {selectedCount > 0 && (
        <div className="bg-teal-50 border-2 border-teal-300 rounded-lg p-3 text-sm text-teal-800 flex items-center justify-between">
          <span><strong>{selectedCount}</strong> factura{selectedCount > 1 ? 's' : ''} | Neto: <strong>${fmt(selectedMonto)}</strong></span>
        </div>
      )}

      {loading && (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div>
      )}

      {/* LOTES VB PENDIENTES */}
      {!loading && lotesVBPend.length > 0 && (
        <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4 space-y-3" data-testid="lotes-vb-pendientes">
          <h2 className="text-sm font-bold text-amber-800 flex items-center gap-2">
            <Package className="w-4 h-4" /> Lotes VB Pendientes de Contabilizar ({lotesVBPend.length})
          </h2>
          {lotesVBPend.map(lote => (
            <div key={lote.lista_id} className="bg-white border rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <span className="font-semibold text-sm">{lote.observaciones || `Lista ${lote.lista_id}`}</span>
                  <span className="ml-2 text-xs text-muted-foreground">{lote.total_items} facturas</span>
                  {lote.fecha_creacion && <span className="ml-2 text-xs text-muted-foreground">{new Date(lote.fecha_creacion).toLocaleDateString('es-CL')}</span>}
                </div>
                <Button size="sm" className="bg-teal-600 hover:bg-teal-700"
                  onClick={() => handleContabilizarLote(lote.lista_id)}
                  disabled={contabLote === lote.lista_id}
                  data-testid={`contab-lote-${lote.lista_id}`}>
                  {contabLote === lote.lista_id
                    ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Procesando...</>
                    : <><Upload className="w-4 h-4 mr-1" /> Contabilizar en Alegra</>}
                </Button>
              </div>
              <StickyHScroll className="border rounded">
                <table className="w-full text-[10px]">
                  <thead className="bg-amber-100">
                    <tr>
                      <th className="px-2 py-1 text-left">Factura</th>
                      <th className="px-2 py-1 text-left">Proveedor</th>
                      <th className="px-2 py-1 text-left">OC</th>
                      <th className="px-2 py-1 text-left">Recepcion</th>
                      <th className="px-2 py-1 text-right">Neto</th>
                      <th className="px-2 py-1 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(lote.items || []).map((item, idx) => (
                      <tr key={idx} className="border-t hover:bg-amber-50/50">
                        <td className="px-2 py-1 font-medium">{item.docmto || item.dte_folio || '-'}</td>
                        <td className="px-2 py-1 truncate max-w-[200px]">{item.nombre_proveedor || item.dte_rut_emisor}</td>
                        <td className="px-2 py-1 text-blue-600">{item.numero_oc || '-'}</td>
                        <td className="px-2 py-1 text-green-600">{item.numero_recepcion || '-'}</td>
                        <td className="px-2 py-1 text-right font-mono">${fmt(item.total_neto)}</td>
                        <td className="px-2 py-1 text-right font-mono font-bold">${fmt(item.dte_total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </StickyHScroll>
            </div>
          ))}
        </div>
      )}

      {/* LIST 1: Manual */}
      {!loading && manualesFiltered.length > 0 && (
        <div data-testid="lista-manuales">
          <div className="flex items-center gap-2 mb-2">
            <h2 className="text-sm font-bold text-indigo-800">Facturas Agregadas Manualmente ({manualesFiltered.length})</h2>
          </div>
          <StickyHScroll className="border border-indigo-200 rounded-lg">
            <table className="w-full text-xs" data-testid="manuales-table">
              <thead>{tableHeaders('bg-indigo-800')}</thead>
              <tbody>{manualesFiltered.map((f, i) => renderRow(f, i, true))}</tbody>
            </table>
          </StickyHScroll>
        </div>
      )}

      {/* LIST 2: Auto */}
      {!loading && filtered.length > 0 && (
        <div data-testid="lista-auto">
          <div className="flex items-center gap-2 mb-2">
            <h2 className="text-sm font-bold text-gray-700">Facturas con VB / DTE Aceptados ({filtered.length})</h2>
            {contabAutoCount > 0 && (
              <span className="text-[10px] bg-green-100 text-green-600 px-2 py-0.5 rounded font-medium">{contabAutoCount} contab.</span>
            )}
            {excedeCount > 0 && (
              <span className="text-[10px] bg-red-100 text-red-600 px-2 py-0.5 rounded font-medium">{excedeCount} exceden OC</span>
            )}
          </div>
          <StickyHScroll className="border rounded-lg">
            <table className="w-full text-xs" data-testid="facturas-contab-table">
              <thead>{tableHeaders('bg-gray-800')}</thead>
              <tbody>{filtered.map((f, i) => renderRow(f, i, false))}</tbody>
            </table>
          </StickyHScroll>
        </div>
      )}

      {!loading && data && filtered.length === 0 && manualesFiltered.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <CheckCircle className="w-12 h-12 mx-auto text-green-400 mb-3" />
          <p className="text-gray-600 font-medium">No hay facturas pendientes de contabilizar</p>
        </div>
      )}

      {showDescartadas && descartadas.length > 0 && (
        <div className="bg-gray-50 border rounded-lg p-4" data-testid="descartadas-section">
          <h3 className="text-xs font-semibold text-gray-500 mb-2">Facturas Descartadas ({descartadas.length})</h3>
          <div className="space-y-1">
            {descartadas.map((d, i) => (
              <div key={i} className="flex items-center justify-between bg-white rounded px-3 py-1.5 text-xs border"
                data-testid={`descartada-row-${d.rut_proveedor || 'srut'}-${d.folio}`}>
                <span>Folio <strong>{d.folio}</strong>{d.rut_proveedor ? <span className="text-gray-400"> · {d.rut_proveedor}</span> : null} - {d.descartado_por} - {fmtFecha(d.fecha)}</span>
                <button onClick={() => handleRestaurar(d.folio, d.rut_proveedor)}
                  className="text-blue-600 hover:text-blue-800 text-[10px] underline"
                  data-testid={`restaurar-${d.rut_proveedor || 'srut'}-${d.folio}`}>Restaurar</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* DTE Detail Modal */}
      <Dialog open={dteModal.open} onOpenChange={(o) => !o && setDteModal({ open: false, folio: null, data: null, operaciones: [], loading: false })}>
        <DialogContent className="max-w-[650px] max-h-[85vh] overflow-y-auto p-4">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-sm">
              <FileText className="w-4 h-4 text-blue-600" /> Documento Tributario Electronico
            </DialogTitle>
          </DialogHeader>
          {dteModal.loading && <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>}
          {dteModal.data && (
            <FacturaViewer dte={dteModal.data} operaciones={dteModal.operaciones || []} />
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setDteModal({ open: false, folio: null, data: null, operaciones: [], loading: false })}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* OC Detail Modal (Issue #2 fix) */}
      <Dialog open={ocModal.open} onOpenChange={(o) => !o && setOcModal({ open: false, data: null, loading: false, notFound: false, ocNumero: null, folio: null })}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-green-600" /> Orden de Compra {ocModal.ocNumero || ocModal.data?.oc?.numero_oc}
            </DialogTitle>
          </DialogHeader>
          {ocModal.loading && <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>}
          {/* OC NOT FOUND state */}
          {ocModal.notFound && (
            <div className="space-y-4" data-testid="oc-not-found-modal">
              <div className="bg-red-50 border border-red-200 rounded-lg p-5 text-center space-y-2">
                <AlertOctagon className="w-10 h-10 text-red-500 mx-auto" />
                <p className="text-sm font-bold text-red-700">OC {ocModal.ocNumero} no existe en el sistema</p>
                <p className="text-xs text-red-600">
                  La factura {ocModal.folio} referencia una Orden de Compra que no fue encontrada en la base de datos.
                </p>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
                <p className="font-medium mb-1">Opciones:</p>
                <ul className="text-xs space-y-1 list-disc pl-4">
                  <li><strong>Rechazar:</strong> Descarta esta factura del listado porque la OC no existe.</li>
                  <li><strong>OC Express:</strong> Crea una OC nueva + Recepcion automatica basada en el DTE.</li>
                </ul>
              </div>
              <div className="flex gap-3 justify-center pt-2">
                <Button variant="destructive" size="sm"
                  onClick={() => handleRechazarPorNoOC(ocModal.folio, ocModal.ocNumero, ocModal.rutEmisor)}
                  data-testid="rechazar-no-oc-btn">
                  <Trash2 className="w-4 h-4 mr-1" /> Rechazar Factura
                </Button>
                <Button size="sm" className="bg-amber-500 hover:bg-amber-600"
                  onClick={() => handleExpressFromOCModal(ocModal.folio)}
                  data-testid="express-from-oc-modal-btn">
                  <Zap className="w-4 h-4 mr-1" /> OC Express + Recepcion
                </Button>
              </div>
            </div>
          )}
          {ocModal.data && (
            <OCViewer
              oc={ocModal.data.oc}
              detalle={ocModal.data.detalle || []}
              proveedor={ocModal.data.proveedor || {}}
              operaciones={ocModal.data.operaciones || []}
              formaPago={ocModal.data.forma_pago || ''}
              esTarjeta={ocModal.data.es_tarjeta || false}
              empresa={empresa}
            />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOcModal({ open: false, data: null, loading: false, notFound: false, ocNumero: null, folio: null })}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <OCExpressModal
        open={expressModal.open}
        onClose={() => { setExpressModal({ open: false, folio: null, reemplazar_oc_actual: false, oc_anterior: null }); setPreviewData(null); setExpressCondMP(EXPRESS_CMP_VACIO); }}
        previewData={previewData}
        previewLoading={previewLoading}
        expressOT={expressOT}
        setExpressOT={setExpressOT}
        expressSolicitante={expressSolicitante}
        setExpressSolicitante={setExpressSolicitante}
        itemsCtas={itemsCtas}
        setItemsCtas={setItemsCtas}
        itemsOts={itemsOts}
        setItemsOts={setItemsOts}
        otList={otList}
        otFiltro={otFiltro}
        setOtFiltro={setOtFiltro}
        loadOts={loadOts}
        expressCondMP={expressCondMP}
        setExpressCondMP={setExpressCondMP}
        expressProcesando={expressProcesando}
        onExpressOC={handleExpressOC}
        onSoloVbAgregar={handleSoloVbAgregar}
        reemplazarOcActual={expressModal.reemplazar_oc_actual}
        ocAnterior={expressModal.oc_anterior}
      />

      <ContabResultModal
        open={resultModal.open}
        data={resultModal.data}
        onClose={() => setResultModal({ open: false, data: null })}
      />

      <PDFViewerModal
        open={pdfModal.open}
        url={pdfModal.url}
        title={pdfModal.title}
        loading={pdfModal.loading}
        onClose={() => {
          if (pdfModal.url) URL.revokeObjectURL(pdfModal.url);
          setPdfModal({ open: false, url: null, title: '', loading: false });
        }}
      />
    </div>
  );
}
